import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

/**
 * Small inline currency icon — the Chillet gold coin. Named `SkullCoin`
 * for backwards-compat with every existing import site; under the hood
 * it now renders the gold coin instead of the skull. The drop-shadow
 * is swapped from teal (skull aesthetic) to gold so it reads as money.
 */
export function SkullCoin({ size = 16, className = "" }) {
  return <ChilletCoinIcon size={size} className={className} style={{ filter: "drop-shadow(0 0 4px rgba(255,196,90,0.55))" }} />;
}

export function Currency({ value, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 font-semibold ${className}`} data-testid="currency-value">
      <SkullCoin size={14} />
      <span className="glow-gold">{(value ?? 0).toLocaleString()}</span>
    </span>
  );
}

export function Loader({ label = "Summoning…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="loader">
      <ChilletSkull className="skull-loader" size={36} />
      <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{label}</span>
    </div>
  );
}

export function PageHeader({ title, subtitle, right, badge }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 fade-up">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl sm:text-4xl glow-teal" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{title}</h1>
          {badge && (
            <span
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-amber-300/40 text-amber-300 bg-amber-300/10 font-semibold"
              data-testid="page-header-badge"
              title="This feature is still in beta — math and UI may change."
            >
              {badge}
            </span>
          )}
        </div>
        {subtitle && <p className="text-[var(--muted)] mt-1 text-sm">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[var(--muted)] mt-1">{hint}</span>}
    </label>
  );
}

export function Empty({ icon: Icon = ChilletSkull, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <Icon size={48} className="wisp text-[var(--teal)] opacity-60 mb-4" />
      <h3 className="text-xl mb-2">{title}</h3>
      {hint && <p className="text-sm text-[var(--muted)] max-w-sm mb-4">{hint}</p>}
      {action}
    </div>
  );
}

export function RarityBadge({ rarity }) {
  const r = (rarity || "common").toLowerCase();
  return (
    <span className={`badge rarity-${r}`} data-testid={`rarity-${r}`}>{r}</span>
  );
}

export function InGameBanner({ server }) {
  return (
    <div className="rounded-md border border-[var(--warning)] bg-[rgba(255,107,53,0.08)] p-3 text-sm flex items-start gap-2" data-testid="in-game-banner">
      <span className="text-[var(--warning)]">⚠</span>
      <div>
        <strong className="text-[var(--warning)]">In-game required.</strong>{" "}
        Join {server || "any Chillet's server"} and type <code className="bg-black/40 px-1 rounded">!claimprize</code> to receive your reward.
      </div>
    </div>
  );
}
